"use client";

import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../../../constants/config";

const DEFAULT_ASSET_FORM = {
  asset_name: "",
  category: "",
  model_number: "",
  serial_number: "",
  vendor_id: "",
  vendor_name: "",
  purchase_date: "",
  purchase_cost: 0,
  warranty_period: "",
  warranty_expiry_date: "",
  assigned_to: "",
  location: "",
  maintenance_schedule: {
    frequency: "",
    next_date: "",
    tasks: [],
  },
  last_maintenance_date: "",
  condition: "New",
  depreciation_rate: 0,
  current_value: 0,
  status: "Active",
  quantity_total: 1,
  quantity_in_service: 1,
  quantity_needs_repair: 0,
};

const DEFAULT_MAINTENANCE_FORM = {
  date: new Date().toISOString().split("T")[0],
  type: "Preventive",
  description: "",
  cost: 0,
  technician: "",
};

function validateQuantityInputs(total, inService, needsRepair) {
  const t = Math.max(1, Number.parseInt(String(total), 10) || 1);
  const s = Math.max(0, Number.parseInt(String(inService), 10) || 0);
  const r = Math.max(0, Number.parseInt(String(needsRepair), 10) || 0);
  if (s + r > t) {
    return {
      ok: false,
      message: "In service + needs repair cannot be more than total units.",
    };
  }
  return { ok: true, t, s, r };
}

export function useAssetsManagement() {
  const [assets, setAssets] = useState([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    category: "all",
    condition: "all",
    status: "all",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: "", content: "" });
  const [sortColumn, setSortColumn] = useState("asset_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [newAsset, setNewAsset] = useState(DEFAULT_ASSET_FORM);
  const [editAsset, setEditAsset] = useState(DEFAULT_ASSET_FORM);
  const [newMaintenance, setNewMaintenance] = useState(DEFAULT_MAINTENANCE_FORM);
  const [vendors, setVendors] = useState([]);

  const categories = [
    "IT Equipment",
    "Lab Equipment",
    "Furniture",
    "Office Equipment",
    "Vehicles",
    "Infrastructure",
  ];
  const conditions = ["New", "Good", "Needs Repair", "Damaged"];
  const statuses = ["Active", "Disposed", "Damaged", "Transferred"];

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(API_ENDPOINTS.vendors, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch vendors: ${response.statusText}`);
      }

      const data = await response.json();
      setVendors(data.data || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const params = new URLSearchParams({
        paginate: "true",
        page: String(currentPage),
        limit: String(itemsPerPage),
        sortBy: String(sortColumn),
        sortOrder: String(sortDirection),
      });

      if (searchTerm?.trim()) params.set("search", searchTerm.trim());
      if (filterOptions.category && filterOptions.category !== "all") {
        params.set("category", filterOptions.category);
      }
      if (filterOptions.condition && filterOptions.condition !== "all") {
        params.set("condition", filterOptions.condition);
      }
      if (filterOptions.status && filterOptions.status !== "all") {
        params.set("status", filterOptions.status);
      }

      const response = await fetch(`${API_ENDPOINTS.assets}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.statusText}`);
      }

      const data = await response.json();
      setAssets(data.data || []);
      setTotalAssets(Number(data.total ?? data.count ?? 0));
    } catch (err) {
      console.error("Error fetching assets:", err);
      setError(err.message);
      setTotalAssets(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [currentPage, itemsPerPage, searchTerm, filterOptions, sortColumn, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterOptions]);

  const handleAddAsset = async () => {
    if (!newAsset.asset_name || !newAsset.category) {
      setMessage({ type: "error", content: "Please fill all required fields" });
      return;
    }

    const q = validateQuantityInputs(
      newAsset.quantity_total,
      newAsset.quantity_in_service,
      newAsset.quantity_needs_repair,
    );
    if (!q.ok) {
      setMessage({ type: "error", content: q.message });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(API_ENDPOINTS.assets, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newAsset,
          quantity_total: q.t,
          quantity_in_service: q.s,
          quantity_needs_repair: q.r,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add asset: ${response.statusText}`);
      }

      const data = await response.json();
      setAssets((prev) => [...prev, data.data]);
      setShowAddForm(false);
      setNewAsset(DEFAULT_ASSET_FORM);
      setMessage({ type: "success", content: "Asset added successfully!" });
    } catch (err) {
      console.error("Error adding asset:", err);
      setMessage({ type: "error", content: `Failed to add asset: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAsset = async () => {
    if (!editAsset.asset_name || !editAsset.category) {
      setMessage({ type: "error", content: "Please fill all required fields" });
      return;
    }

    const q = validateQuantityInputs(
      editAsset.quantity_total,
      editAsset.quantity_in_service,
      editAsset.quantity_needs_repair,
    );
    if (!q.ok) {
      setMessage({ type: "error", content: q.message });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(API_ENDPOINTS.asset(selectedAsset.asset_id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...editAsset,
          quantity_total: q.t,
          quantity_in_service: q.s,
          quantity_needs_repair: q.r,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update asset: ${response.statusText}`);
      }

      const data = await response.json();
      setAssets((prev) =>
        prev.map((asset) =>
          asset.asset_id === selectedAsset.asset_id ? data.data : asset,
        ),
      );

      setShowEditForm(false);
      setSelectedAsset(null);
      setMessage({ type: "success", content: "Asset updated successfully!" });
    } catch (err) {
      console.error("Error updating asset:", err);
      setMessage({
        type: "error",
        content: `Failed to update asset: ${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMaintenance = async () => {
    if (!selectedAsset) {
      setMessage({ type: "error", content: "Please select an asset first" });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(API_ENDPOINTS.assetMaintenance(selectedAsset.asset_id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newMaintenance),
      });

      if (!response.ok) {
        throw new Error(`Failed to add maintenance record: ${response.statusText}`);
      }

      const data = await response.json();
      setAssets((prev) =>
        prev.map((asset) =>
          asset.asset_id === selectedAsset.asset_id ? data.data : asset,
        ),
      );
      setShowMaintenanceForm(false);
      setNewMaintenance(DEFAULT_MAINTENANCE_FORM);
      setMessage({
        type: "success",
        content: "Maintenance record added successfully!",
      });
    } catch (err) {
      console.error("Error adding maintenance record:", err);
      setMessage({
        type: "error",
        content: `Failed to add maintenance record: ${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditForm = (asset) => {
    setSelectedAsset(asset);
    setEditAsset({
      ...asset,
      quantity_total: asset.quantity_total ?? 1,
      quantity_in_service: asset.quantity_in_service ?? asset.quantity_total ?? 1,
      quantity_needs_repair: asset.quantity_needs_repair ?? 0,
      purchase_date: asset.purchase_date
        ? new Date(asset.purchase_date).toISOString().split("T")[0]
        : "",
      warranty_expiry_date: asset.warranty_expiry_date
        ? new Date(asset.warranty_expiry_date).toISOString().split("T")[0]
        : "",
      last_maintenance_date: asset.last_maintenance_date
        ? new Date(asset.last_maintenance_date).toISOString().split("T")[0]
        : "",
      maintenance_schedule: {
        ...asset.maintenance_schedule,
        next_date:
          asset.maintenance_schedule && asset.maintenance_schedule.next_date
            ? new Date(asset.maintenance_schedule.next_date)
                .toISOString()
                .split("T")[0]
            : "",
      },
    });
    setShowEditForm(true);
  };

  const openViewDialog = (asset) => {
    setSelectedAsset(asset);
    setShowViewDialog(true);
  };

  const handleAssetSort = (column) => {
    const newDirection =
      sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
    setCurrentPage(1);
  };

  return {
    assets,
    totalAssets,
    selectedAsset,
    setSelectedAsset,
    searchTerm,
    setSearchTerm,
    showAddForm,
    setShowAddForm,
    showEditForm,
    setShowEditForm,
    showViewDialog,
    setShowViewDialog,
    showMaintenanceForm,
    setShowMaintenanceForm,
    filterOptions,
    setFilterOptions,
    loading,
    error,
    message,
    sortColumn,
    sortDirection,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    categories,
    conditions,
    statuses,
    newAsset,
    setNewAsset,
    editAsset,
    setEditAsset,
    newMaintenance,
    setNewMaintenance,
    vendors,
    fetchAssets,
    handleAddAsset,
    handleEditAsset,
    handleAddMaintenance,
    openEditForm,
    openViewDialog,
    handleAssetSort,
  };
}
